import * as React from 'react'
import {
    AppBar,
    Box,
    Button,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Toolbar,
    Typography,
    useMediaQuery
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { keyframes } from '@mui/system'
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded'
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import HubRoundedIcon from '@mui/icons-material/HubRounded'
import PaidRoundedIcon from '@mui/icons-material/PaidRounded'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'

import { CHAT_LANGUAGE_OPTIONS } from '../constants/chatLanguages'
import useAutoTranslate from '../hooks/useAutoTranslate'
import { useSmoothLiveNow } from '../utils/liveClock'

const drawerWidth = 320

const formatSubscriptionRemaining = (accountInfo, now) => {
    if (!accountInfo)
        return { label: 'Loading...', detail: 'Checking account info' }

    if (accountInfo.hasLifetimeAccess)
        return { label: 'Lifetime', detail: 'Unlimited access' }

    if (!accountInfo.subscriptionEndsAt)
        return { label: 'Not set', detail: 'No expiry configured' }

    const expiresAt = new Date(accountInfo.subscriptionEndsAt).getTime()
    if (!Number.isFinite(expiresAt))
        return { label: 'Unknown', detail: 'Invalid subscription date' }

    const remainingMs = expiresAt - now
    if (remainingMs <= 0)
        return { label: 'Expired', detail: `Expired on ${new Date(expiresAt).toLocaleDateString()}` }

    const totalSeconds = Math.floor(remainingMs / 1000)
    const days = Math.floor(totalSeconds / (24 * 60 * 60))
    const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    let label = ''
    if (days > 0)
        label = `${days}d ${hours}h left`
    else if (hours > 0)
        label = `${hours}h ${minutes}m ${seconds}s left`
    else if (minutes > 0)
        label = `${minutes}m ${seconds}s left`
    else
        label = `${Math.max(0, seconds)}s left`

    return {
        label,
        detail: `Expires ${new Date(expiresAt).toLocaleString()}`
    }
}

const buildNavigationItems = ({ isAdmin, canAccessModeratorView, t }) => {
    const items = [
        {
            key: 'dashboard',
            label: t('Dashboard'),
            icon: <DashboardRoundedIcon />,
            type: 'view'
        }
    ]

    if (isAdmin) {
        items.push({
            key: 'admin',
            label: t('Admin'),
            icon: <AdminPanelSettingsRoundedIcon />,
            type: 'view'
        })
    } else if (canAccessModeratorView) {
        items.push({
            key: 'moderator',
            label: t('Moderator'),
            icon: <AdminPanelSettingsRoundedIcon />,
            type: 'view'
        })
    }

    return [
        ...items,
        {
            key: 'attack-templates',
            label: t('Attack Templates'),
            icon: <DescriptionRoundedIcon />,
            type: 'view'
        },
        {
            key: 'battle-reports',
            label: t('Battle Reports'),
            icon: <ReceiptLongRoundedIcon />,
            type: 'view'
        },
        {
            key: 'licenses',
            label: t('Licenses'),
            icon: <HubRoundedIcon />,
            type: 'placeholder'
        },
        {
            key: 'pricing',
            label: t('Pricing'),
            icon: <PaidRoundedIcon />,
            type: 'view',
            premium: true
        }
    ]
}

const resolveDisplayUsername = accountInfo => {
    const username = String(accountInfo?.username ?? '').trim()
    if (username)
        return username

    const email = String(accountInfo?.email ?? '').trim()
    if (email)
        return email

    return ''
}

const readInstallState = () => {
    if (typeof window === 'undefined')
        return { canInstall: false, installed: false }

    return {
        canInstall: Boolean(window.ggeInstallPromptState?.canInstall),
        installed: Boolean(window.ggeInstallPromptState?.installed)
    }
}

const navButtonSx = active => ({
    px: 2,
    py: 1.15,
    borderRadius: 3,
    color: active ? 'primary.light' : 'rgba(238,242,255,0.72)',
    backgroundColor: active ? 'rgba(67, 128, 255, 0.16)' : 'transparent',
    border: active ? '1px solid rgba(67, 128, 255, 0.34)' : '1px solid transparent',
    textTransform: 'none',
    fontWeight: 700,
    minWidth: 'auto',
    '&:hover': {
        backgroundColor: active ? 'rgba(67, 128, 255, 0.22)' : 'rgba(255,255,255,0.05)',
        borderColor: active ? 'rgba(67, 128, 255, 0.42)' : 'rgba(255,255,255,0.08)'
    }
})

const iconButtonSx = {
    width: 40,
    height: 40,
    borderRadius: 2.8,
    border: '1px solid rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    color: 'text.primary',
    '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.1)'
    }
}

const pricingSparkle = keyframes`
    0%, 100% {
        opacity: 0.48;
        transform: scale(0.82) rotate(0deg);
    }
    50% {
        opacity: 1;
        transform: scale(1) rotate(12deg);
    }
`

const pricingIconSx = {
    width: 28,
    height: 28,
    borderRadius: '8px',
    display: 'grid',
    placeItems: 'center',
    color: '#101621',
    backgroundColor: '#f4c95d',
    border: '1px solid rgba(255, 231, 164, 0.8)',
    boxShadow: '0 4px 14px rgba(244, 201, 93, 0.2)',
    '& svg': {
        fontSize: 18
    }
}

const pricingSidebarSx = {
    borderRadius: '8px',
    py: 1.05,
    color: '#f7e3aa',
    backgroundColor: 'rgba(244, 201, 93, 0.08)',
    border: '1px solid rgba(244, 201, 93, 0.25)',
    boxShadow: 'inset 3px 0 0 #f4c95d',
    overflow: 'hidden',
    '&:hover': {
        backgroundColor: 'rgba(244, 201, 93, 0.14)',
        borderColor: 'rgba(244, 201, 93, 0.44)',
        transform: 'translateX(2px)'
    },
    transition: 'transform 160ms ease, background-color 160ms ease, border-color 160ms ease'
}

const pricingTopNavSx = {
    px: 1.35,
    py: 0.8,
    minWidth: 'auto',
    borderRadius: '8px',
    color: '#f7e3aa',
    backgroundColor: 'rgba(244, 201, 93, 0.08)',
    border: '1px solid rgba(244, 201, 93, 0.24)',
    textTransform: 'none',
    fontWeight: 800,
    '& .MuiButton-startIcon': {
        mr: 0.8
    },
    '&:hover': {
        color: '#fff2c9',
        backgroundColor: 'rgba(244, 201, 93, 0.15)',
        borderColor: 'rgba(244, 201, 93, 0.46)',
        transform: 'translateY(-1px)'
    },
    transition: 'transform 160ms ease, background-color 160ms ease, border-color 160ms ease'
}

function PricingIcon() {
    return (
        <Box sx={pricingIconSx}>
            <PaidRoundedIcon />
        </Box>
    )
}

export default function MainLayout({
    children,
    currentView = 'dashboard',
    isAdmin,
    canAccessModeratorView,
    onViewChange,
    accountInfo,
    liveNow,
    appLanguage = 'en',
    onAppLanguageChange
}) {
    const theme = useTheme()
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
    const [mobileOpen, setMobileOpen] = React.useState(false)
    const [languageAnchorEl, setLanguageAnchorEl] = React.useState(null)
    const [accountAnchorEl, setAccountAnchorEl] = React.useState(null)
    const [installState, setInstallState] = React.useState(() => readInstallState())

    React.useEffect(() => {
        if (typeof window === 'undefined')
            return undefined

        const syncInstallState = event => {
            const nextState = event?.detail && typeof event.detail === 'object' ?
                event.detail :
                readInstallState()

            setInstallState({
                canInstall: Boolean(nextState?.canInstall),
                installed: Boolean(nextState?.installed)
            })
        }

        syncInstallState()
        window.addEventListener('gge-install-state', syncInstallState)
        return () => window.removeEventListener('gge-install-state', syncInstallState)
    }, [])

    const effectiveNow = useSmoothLiveNow(liveNow)
    const subscription = formatSubscriptionRemaining(accountInfo, effectiveNow)
    const languageMenuOpen = Boolean(languageAnchorEl)
    const accountMenuOpen = Boolean(accountAnchorEl)
    const displayUsername = resolveDisplayUsername(accountInfo)

    const t = useAutoTranslate(appLanguage, [
        'Dashboard',
        'Admin',
        'Moderator',
        'Attack Templates',
        'Licenses',
        'Pricing',
        'Contact',
        'Bot Management Platform',
        'User',
        'Administrator',
        'Moderator',
        'Operator',
        'Unknown user',
        'Loading account',
        'Loading role',
        'Language',
        'My Account',
        'Subscription',
        'Install App',
        'Installed',
        'Logout',
        'Open menu',
        subscription.label,
        subscription.detail
    ])

    const navigationItems = React.useMemo(
        () => buildNavigationItems({ isAdmin, canAccessModeratorView, t }),
        [canAccessModeratorView, isAdmin, t]
    )

    const normalizedRole = String(accountInfo?.role ?? '').trim().toLowerCase()
    const roleLabel =
        isAdmin || normalizedRole === 'administrator' ? t('Administrator') :
            normalizedRole === 'moderator' ? t('Moderator') :
                canAccessModeratorView ? t('Moderator') :
                normalizedRole === 'operator' ? t('Operator') :
                    accountInfo ? t('User') : t('Loading role')

    const handleLogout = () => {
        document.cookie = 'uuid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
        window.location.href = 'signin.html'
    }
    const handleInstallApp = async () => {
        if (typeof window === 'undefined' || typeof window.ggeInstallApp !== 'function')
            return

        try {
            await window.ggeInstallApp()
        }
        finally {
            setInstallState(readInstallState())
            setAccountAnchorEl(null)
        }
    }

    const handleNavigation = item => {
        if (item.type === 'view')
            onViewChange?.(item.key)

        if (!isDesktop)
            setMobileOpen(false)
    }

    const drawerContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'background.paper' }}>
            <Box sx={{ px: 2.5, py: 2.5, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                        sx={{
                            width: 46,
                            height: 46,
                            borderRadius: 2.8,
                            display: 'grid',
                            placeItems: 'center',
                            background: 'linear-gradient(180deg, #4b93ff 0%, #2b6cdf 100%)',
                            color: '#fff'
                        }}
                    >
                        <SmartToyRoundedIcon />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" sx={{ lineHeight: 1.1 }}>
                            St0rm Bot Manager
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('Bot Management Platform')}
                        </Typography>
                    </Box>
                </Stack>
            </Box>

            <List sx={{ px: 1.5, py: 2, display: 'grid', gap: 0.6 }}>
                {navigationItems.map(item => {
                    const active = item.type === 'view' && currentView === item.key
                    const premium = Boolean(item.premium)

                    return (
                        <ListItemButton
                            key={item.key}
                            onClick={() => handleNavigation(item)}
                            sx={premium ? pricingSidebarSx : {
                                borderRadius: 3,
                                py: 1.15,
                                backgroundColor: active ? 'rgba(67, 128, 255, 0.16)' : 'transparent',
                                border: active ? '1px solid rgba(67, 128, 255, 0.34)' : '1px solid transparent',
                                '&:hover': {
                                    backgroundColor: active ? 'rgba(67, 128, 255, 0.22)' : 'rgba(255,255,255,0.05)'
                                }
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: premium ? 42 : 38, color: active ? 'primary.light' : 'text.secondary' }}>
                                {premium ? <PricingIcon /> : item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    fontWeight: premium ? 800 : 700,
                                    color: premium ? '#f7e3aa' : (active ? 'primary.light' : 'text.primary')
                                }}
                            />
                            {premium && (
                                <AutoAwesomeRoundedIcon
                                    sx={{
                                        ml: 1,
                                        fontSize: 17,
                                        color: '#f4c95d',
                                        animation: `${pricingSparkle} 2.8s ease-in-out infinite`,
                                        '@media (prefers-reduced-motion: reduce)': {
                                            animation: 'none'
                                        }
                                    }}
                                />
                            )}
                        </ListItemButton>
                    )
                })}
            </List>

            <Box sx={{ mt: 'auto', px: 2.5, py: 2.5, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <Stack spacing={1.2}>
                    <Button
                        variant="outlined"
                        color="inherit"
                        startIcon={<ChatBubbleOutlineRoundedIcon />}
                        sx={{ justifyContent: 'flex-start' }}
                    >
                        {t('Contact')}
                    </Button>

                    <Button
                        variant="outlined"
                        color="inherit"
                        startIcon={<LanguageRoundedIcon />}
                        onClick={event => setLanguageAnchorEl(event.currentTarget)}
                        sx={{ justifyContent: 'flex-start' }}
                    >
                        {t('Language')}
                    </Button>

                    {(installState.canInstall || installState.installed) && (
                        <Button
                            variant="outlined"
                            color="inherit"
                            startIcon={<DownloadRoundedIcon />}
                            onClick={handleInstallApp}
                            disabled={installState.installed}
                            sx={{ justifyContent: 'flex-start' }}
                        >
                            {installState.installed ? t('Installed') : t('Install App')}
                        </Button>
                    )}

                    <Box
                        sx={{
                            borderRadius: 3,
                            border: '1px solid rgba(255,255,255,0.06)',
                            backgroundColor: 'rgba(255,255,255,0.03)',
                            p: 1.5
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {displayUsername || t('Loading account')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.2 }}>
                            {roleLabel}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'primary.light', display: 'block', mt: 1 }}>
                            {t(subscription.label)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.2 }}>
                            {t(subscription.detail)}
                        </Typography>
                    </Box>

                    <Button
                        variant="contained"
                        color="inherit"
                        startIcon={<LogoutRoundedIcon />}
                        onClick={handleLogout}
                        sx={{
                            justifyContent: 'flex-start',
                            backgroundColor: 'rgba(255,255,255,0.08)',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' }
                        }}
                    >
                        {t('Logout')}
                    </Button>
                </Stack>
            </Box>
        </Box>
    )

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'transparent' }}>
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    backgroundColor: 'rgba(14, 22, 37, 0.9)',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    backdropFilter: 'blur(14px)'
                }}
            >
                <Toolbar sx={{ minHeight: { xs: 72, md: 78 }, px: { xs: 1.5, md: 3 }, gap: { xs: 1, md: 2 } }}>
                    {!isDesktop && (
                        <IconButton
                            color="inherit"
                            onClick={() => setMobileOpen(true)}
                            sx={{ ...iconButtonSx, mr: 0.5 }}
                            aria-label={t('Open menu')}
                        >
                            <MenuRoundedIcon />
                        </IconButton>
                    )}

                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 2.8,
                                display: 'grid',
                                placeItems: 'center',
                                background: 'linear-gradient(180deg, #4b93ff 0%, #2b6cdf 100%)',
                                color: '#fff',
                                flexShrink: 0
                            }}
                        >
                            <SmartToyRoundedIcon />
                        </Box>
                        <Box sx={{ minWidth: 0, display: { xs: 'none', sm: 'block' } }}>
                            <Typography variant="h6" sx={{ lineHeight: 1.05, whiteSpace: 'nowrap' }}>
                                St0rm Bot Manager
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                {t('Bot Management Platform')}
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        sx={{
                            ml: { xs: 0, lg: 3 },
                            flex: 1,
                            minWidth: 0,
                            overflowX: 'auto',
                            display: { xs: 'none', lg: 'flex' }
                        }}
                    >
                        {navigationItems.map(item => {
                            const active = item.type === 'view' && currentView === item.key
                            const premium = Boolean(item.premium)

                            return (
                                <Button
                                    key={item.key}
                                    disableElevation
                                    startIcon={premium ? <PricingIcon /> : item.icon}
                                    onClick={() => handleNavigation(item)}
                                    sx={premium ? pricingTopNavSx : navButtonSx(active)}
                                >
                                    {item.label}
                                    {premium && (
                                        <AutoAwesomeRoundedIcon
                                            sx={{
                                                ml: 0.7,
                                                fontSize: 15,
                                                color: '#f4c95d',
                                                animation: `${pricingSparkle} 2.8s ease-in-out infinite`,
                                                '@media (prefers-reduced-motion: reduce)': {
                                                    animation: 'none'
                                                }
                                            }}
                                        />
                                    )}
                                </Button>
                            )
                        })}
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
                        <Button
                            startIcon={<ChatBubbleOutlineRoundedIcon />}
                            color="inherit"
                            sx={{
                                display: { xs: 'none', md: 'inline-flex' },
                                textTransform: 'none',
                                color: 'rgba(238,242,255,0.78)',
                                fontWeight: 600
                            }}
                        >
                            {t('Contact')}
                        </Button>

                        <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'right', minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                {displayUsername || t('Loading account')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.1 }}>
                                {roleLabel}
                            </Typography>
                        </Box>

                        <IconButton
                            color="inherit"
                            onClick={event => setAccountAnchorEl(event.currentTarget)}
                            sx={iconButtonSx}
                        >
                            <SettingsRoundedIcon />
                        </IconButton>

                        {installState.canInstall && (
                            <IconButton
                                color="inherit"
                                onClick={handleInstallApp}
                                sx={iconButtonSx}
                                title={t('Install App')}
                            >
                                <DownloadRoundedIcon />
                            </IconButton>
                        )}

                        <IconButton
                            color="inherit"
                            onClick={event => setLanguageAnchorEl(event.currentTarget)}
                            sx={iconButtonSx}
                        >
                            <LanguageRoundedIcon />
                        </IconButton>

                        <IconButton color="inherit" onClick={handleLogout} sx={{ ...iconButtonSx, display: { xs: 'none', sm: 'inline-flex' } }}>
                            <LogoutRoundedIcon />
                        </IconButton>
                    </Stack>
                </Toolbar>
            </AppBar>

            <Drawer
                variant="temporary"
                open={!isDesktop && mobileOpen}
                onClose={() => setMobileOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', lg: 'none' },
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        backgroundImage: 'none'
                    }
                }}
            >
                {drawerContent}
            </Drawer>

            <Menu
                anchorEl={languageAnchorEl}
                open={languageMenuOpen}
                onClose={() => setLanguageAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {CHAT_LANGUAGE_OPTIONS.map(option => (
                    <MenuItem
                        key={option.code}
                        selected={option.code === appLanguage}
                        onClick={() => {
                            onAppLanguageChange?.(option.code)
                            setLanguageAnchorEl(null)
                        }}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>

            <Menu
                anchorEl={accountAnchorEl}
                open={accountMenuOpen}
                onClose={() => setAccountAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { width: 280, borderRadius: 3 } }}
            >
                <Box sx={{ px: 2, py: 1.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {displayUsername || t('Loading account')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                        {roleLabel}
                    </Typography>
                    {accountInfo?.email && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.7 }}>
                            {accountInfo.email}
                        </Typography>
                    )}
                </Box>

                <Divider />

                <Box sx={{ px: 2, py: 1.4 }}>
                    <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <ScheduleRoundedIcon sx={{ fontSize: 18, mt: 0.15, color: 'primary.light' }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.light' }}>
                                {t(subscription.label)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.3, lineHeight: 1.5 }}>
                                {t(subscription.detail)}
                            </Typography>
                        </Box>
                    </Stack>
                </Box>

                <Divider />

                {isAdmin && (
                    <MenuItem
                        onClick={() => {
                            onViewChange?.(currentView === 'admin' ? 'dashboard' : 'admin')
                            setAccountAnchorEl(null)
                        }}
                    >
                        <ListItemIcon>
                            <AdminPanelSettingsRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={currentView === 'admin' ? t('Dashboard') : t('Admin')} />
                    </MenuItem>
                )}

                {!isAdmin && canAccessModeratorView && (
                    <MenuItem
                        onClick={() => {
                            onViewChange?.(currentView === 'moderator' ? 'dashboard' : 'moderator')
                            setAccountAnchorEl(null)
                        }}
                    >
                        <ListItemIcon>
                            <AdminPanelSettingsRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={currentView === 'moderator' ? t('Dashboard') : t('Moderator')} />
                    </MenuItem>
                )}

                <MenuItem onClick={() => setAccountAnchorEl(null)}>
                    <ListItemIcon>
                        <PersonRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('My Account')} />
                </MenuItem>

                {(installState.canInstall || installState.installed) && (
                    <MenuItem
                        onClick={handleInstallApp}
                        disabled={installState.installed}
                    >
                        <ListItemIcon>
                            <DownloadRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={installState.installed ? t('Installed') : t('Install App')} />
                    </MenuItem>
                )}

                <MenuItem
                    onClick={() => {
                        setAccountAnchorEl(null)
                        handleLogout()
                    }}
                >
                    <ListItemIcon>
                        <LogoutRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('Logout')} />
                </MenuItem>
            </Menu>

            <Box
                component="main"
                sx={{
                    pt: { xs: '92px', md: '102px' },
                    px: { xs: 2, md: 3.75 },
                    pb: 5
                }}
            >
                {children}
            </Box>
        </Box>
    )
}
